import { useState } from 'react';
import { trpc } from '@/lib/trpc';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { History, User, Calendar, ArrowRight, Plus, Trash2, Edit, Move, Archive, RotateCcw } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface ChangeHistoryPanelProps {
  boardId: number;
  itemId?: number;
}

const CHANGE_TYPE_ICONS = {
  create: Plus,
  update: Edit,
  delete: Trash2,
  move: Move,
  archive: Archive,
  restore: RotateCcw,
};

const CHANGE_TYPE_LABELS = {
  create: 'Criou',
  update: 'Atualizou',
  delete: 'Deletou',
  move: 'Moveu',
  archive: 'Arquivou',
  restore: 'Restaurou',
};

const CHANGE_TYPE_COLORS = {
  create: 'bg-green-500/10 text-green-700 border-green-500/20',
  update: 'bg-blue-500/10 text-blue-700 border-blue-500/20',
  delete: 'bg-red-500/10 text-red-700 border-red-500/20',
  move: 'bg-purple-500/10 text-purple-700 border-purple-500/20',
  archive: 'bg-orange-500/10 text-orange-700 border-orange-500/20',
  restore: 'bg-cyan-500/10 text-cyan-700 border-cyan-500/20',
};

export function ChangeHistoryPanel({ boardId, itemId }: ChangeHistoryPanelProps) {
  const [limit, setLimit] = useState(100);

  const { data: history, isLoading } = itemId
    ? trpc.taskManager.changeHistory.byItem.useQuery({ itemId })
    : trpc.taskManager.changeHistory.byBoard.useQuery({ boardId, limit });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Carregando histórico...</div>
      </div>
    );
  }

  if (!history || history.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
        <History className="h-12 w-12 mb-2 opacity-50" />
        <p>Nenhuma alteração registrada</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between p-4 border-b">
        <div className="flex items-center gap-2">
          <History className="h-5 w-5" />
          <h3 className="font-semibold">Histórico de Alterações</h3>
          <Badge variant="secondary">{history.length}</Badge>
        </div>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-4 space-y-3">
          {history.map((change: any) => {
            const Icon = CHANGE_TYPE_ICONS[change.change_type as keyof typeof CHANGE_TYPE_ICONS];
            const label = CHANGE_TYPE_LABELS[change.change_type as keyof typeof CHANGE_TYPE_LABELS];
            const colorClass = CHANGE_TYPE_COLORS[change.change_type as keyof typeof CHANGE_TYPE_COLORS];

            return (
              <div
                key={change.id}
                className="border rounded-lg p-3 hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg border ${colorClass}`}>
                    <Icon className="h-4 w-4" />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-sm">{change.user_name}</span>
                      <span className="text-xs text-muted-foreground">{label}</span>
                      <Badge variant="outline" className="text-xs">
                        {change.entity_type}
                      </Badge>
                    </div>

                    {change.field_name && (
                      <div className="text-sm text-muted-foreground mb-2">
                        Campo: <span className="font-medium text-foreground">{change.field_name}</span>
                      </div>
                    )}

                    {(change.old_value || change.new_value) && (
                      <div className="flex items-center gap-2 text-xs">
                        {change.old_value && (
                          <div className="px-2 py-1 bg-red-500/10 text-red-700 rounded border border-red-500/20 truncate max-w-[200px]">
                            {change.old_value}
                          </div>
                        )}
                        {change.old_value && change.new_value && (
                          <ArrowRight className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                        )}
                        {change.new_value && (
                          <div className="px-2 py-1 bg-green-500/10 text-green-700 rounded border border-green-500/20 truncate max-w-[200px]">
                            {change.new_value}
                          </div>
                        )}
                      </div>
                    )}

                    <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                      <Calendar className="h-3 w-3" />
                      {formatDistanceToNow(new Date(change.created_at), {
                        addSuffix: true,
                        locale: ptBR,
                      })}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </ScrollArea>

      {!itemId && history.length >= limit && (
        <div className="p-4 border-t">
          <Button
            variant="outline"
            className="w-full"
            onClick={() => setLimit((prev) => prev + 100)}
          >
            Carregar mais
          </Button>
        </div>
      )}
    </div>
  );
}
